import { Injectable, inject } from '@angular/core';
import {
  Firestore, collection, doc, addDoc, updateDoc, getDoc, getDocs,
  query, where, orderBy, limit, serverTimestamp,
} from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { CollaboratorAvailability, CollaboratorRole, Money } from '@app/core/models';

import {
  PartnerApplication,
  PartnerApplicationStatus,
  PartnerLink,
  PartnerProjectEntry,
} from '../models/partner.models';
import { CollaboratorService } from '@app/core/services/collaborator.service';
import { PartnerRoleConfigService } from './partner-role-config';

const APPLICATIONS = 'partnerApplications';

/**
 * Data layer for partner (Execution Partner) applications — submission
 * through to admin approve/reject, and the resulting handoff into
 * CollaboratorService.
 *
 * FIRESTORE INDEXES: two queries below filter on `uid` together with a
 * second clause (`status in [...]` for the duplicate check, `orderBy
 * createdAt` for history). Firestore will very likely require a composite
 * index for each the first time they run in a real project — if you see a
 * "The query requires an index" error, Firestore's error message includes
 * a direct console link to create it with one click. Not something I can
 * pre-verify without access to your actual index config.
 */
@Injectable({ providedIn: 'root' })
export class PartnerApplicationService {
  private readonly firestore = inject(Firestore);
  private readonly storage = inject(Storage);
  private readonly collaborators = inject(CollaboratorService);
  private readonly roleConfig = inject(PartnerRoleConfigService);

  /* ── Applicant-facing ──────────────────────────────────────────────── */

  /**
   * Submits a new application. Blocks a duplicate submission ONLY while a
   * prior application from this uid is still 'pending' or already
   * 'approved' — a previously 'rejected' applicant is free to re-apply
   * (e.g. after gaining more experience), which is why this checks status
   * rather than mere existence.
   *
   * Uploads the resume file to Storage first; the application doc only
   * ever stores the resulting download URL, never the raw File.
   */
  async submitApplication(input: {
    uid: string;
    email: string;
    fullName: string;
    phone?: string;
    location?: string;
    role: CollaboratorRole;
    headline: string;
    bio: string;
    yearsExperience: number;
    availability: CollaboratorAvailability;
    resumeFile: File;
    links: PartnerLink[];
    notableProjects: PartnerProjectEntry[];
    roleFieldValues: Record<string, unknown>;
  }): Promise<string> {
    const blocked = await this.hasBlockingApplication(input.uid);
    if (blocked) {
      throw new Error(
        'You already have an application that is pending review or already approved.',
      );
    }

    const resumeUrl = await this.uploadResume(input.uid, input.resumeFile);

    const ref = await addDoc(collection(this.firestore, APPLICATIONS), {
      uid: input.uid,
      email: input.email,
      fullName: input.fullName,
      phone: input.phone ?? null,
      location: input.location ?? null,
      role: input.role,
      headline: input.headline,
      bio: input.bio,
      yearsExperience: input.yearsExperience,
      availability: input.availability,
      resumeUrl,
      links: input.links ?? [],
      notableProjects: this.sanitizeProjects(input.notableProjects),
      roleFieldValues: input.roleFieldValues ?? {},
      status: 'pending' as PartnerApplicationStatus,
      reviewedBy: null,
      reviewedAt: null,
      rejectionReason: null,
      collaboratorId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }

  /**
   * Firestore's addDoc/updateDoc reject `undefined` anywhere in the
   * payload — including nested inside array items, not just top-level
   * fields. PartnerProjectEntry.url is optional, and the apply form sends
   * `url: undefined` when it's left blank, so writing notableProjects
   * straight through was the actual cause of the "Unsupported field value:
   * undefined" error. Fix: omit the key entirely when there's no url,
   * rather than writing `undefined` (or even `null`, which would create a
   * mismatch against the `url?: string` type on read-back).
   */
  private sanitizeProjects(projects: PartnerProjectEntry[]): Record<string, unknown>[] {
    return (projects ?? []).map((p) => {
      const entry: Record<string, unknown> = {
        id: p.id,
        name: p.name,
        description: p.description,
      };
      if (p.url) entry['url'] = p.url;
      return entry;
    });
  }

  /** True if this uid has an application that's still pending or already
   *  approved — the only cases that should block a fresh submission. */
  private async hasBlockingApplication(uid: string): Promise<boolean> {
    const q = query(
      collection(this.firestore, APPLICATIONS),
      where('uid', '==', uid),
      where('status', 'in', ['pending', 'approved'] as PartnerApplicationStatus[]),
      limit(1),
    );
    const snap = await getDocs(q);
    return !snap.empty;
  }

  /** All applications this uid has ever submitted, most recent first —
   *  drives the "applied" confirmation page and any status-check UI. */
  async getMyApplications(uid: string): Promise<PartnerApplication[]> {
    const q = query(
      collection(this.firestore, APPLICATIONS),
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => this.toApplication(d.id, d.data()));
  }

  /** Convenience — the most recent application this uid has submitted, or
   *  null if they've never applied. */
  async getMyLatestApplication(uid: string): Promise<PartnerApplication | null> {
    const all = await this.getMyApplications(uid);
    return all[0] ?? null;
  }

  private async uploadResume(uid: string, file: File): Promise<string> {
    const path = `partner-resumes/${uid}/${Date.now()}-${file.name}`;
    const fileRef = ref(this.storage, path);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  }

  /* ── Admin-facing ──────────────────────────────────────────────────── */

  /** The review queue — oldest first, so admin naturally works through it
   *  in submission order. */
  async listPending(): Promise<PartnerApplication[]> {
    const q = query(
      collection(this.firestore, APPLICATIONS),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => this.toApplication(d.id, d.data()));
  }

  /** All applications regardless of status, most recent first — for an
   *  admin "all applications" history view alongside the pending queue. */
  async listAll(): Promise<PartnerApplication[]> {
    const q = query(
      collection(this.firestore, APPLICATIONS),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => this.toApplication(d.id, d.data()));
  }

  async getById(id: string): Promise<PartnerApplication | null> {
    const snap = await getDoc(doc(this.firestore, APPLICATIONS, id));
    if (!snap.exists()) return null;
    return this.toApplication(snap.id, snap.data());
  }

  /**
   * Approves an application and turns it into a live Collaborator record.
   *
   * Order matters here: the Collaborator write happens BEFORE the
   * application is marked 'approved'. If the Collaborator step throws, the
   * application is left exactly as it was (still 'pending', safe to retry)
   * rather than ending up "approved" with no resulting profile. This is
   * two separate Firestore writes, not a single transaction — a true
   * all-or-nothing guarantee would mean reworking CollaboratorService's
   * methods to accept a shared transaction, which felt like more than v1
   * needs; flagging it as a known limitation rather than silently assuming
   * it's fully atomic.
   *
   * Checks for an existing Collaborator (by uid, falling back to email —
   * same resolution CollaboratorService already uses for login) before
   * deciding create vs. update, so someone who was manually admin-added
   * earlier and later self-applies doesn't end up with two records.
   *
   * `payoutRatePerPoint` is supplied by the admin at approval time in the
   * review UI — applications don't set their own rate, matching how the
   * existing manual admin-add flow already works.
   */
  async approve(
    applicationId: string,
    adminUid: string,
    payoutRatePerPoint: Money,
  ): Promise<string> {
    const application = await this.getById(applicationId);
    if (!application) throw new Error('Application not found.');
    if (application.status !== 'pending') {
      throw new Error(`Only pending applications can be approved (current status: ${application.status}).`);
    }

    const existing = await this.collaborators.resolvePartnerAccess(
      application.uid,
      application.email,
    );

    const skills = this.deriveSkills(application);
    let collaboratorId: string;

    if (existing) {
      await this.collaborators.update(existing.id, {
        displayName: application.fullName,
        role: application.role,
        payoutRatePerPoint,
        headline: application.headline,
        skills,
        bio: application.bio,
        yearsExperience: application.yearsExperience,
        availability: application.availability,
        email: application.email,
        location: application.location,
        links: application.links,
        resumeUrl: application.resumeUrl,
        notableProjects: application.notableProjects,
        uid: application.uid,
      });
      // Approval should always leave the collaborator eligible for work,
      // even if the existing record was previously paused/offboarded.
      await this.collaborators.reactivate(existing.id);
      collaboratorId = existing.id;
    } else {
      collaboratorId = await this.collaborators.create({
        displayName: application.fullName,
        role: application.role,
        payoutRatePerPoint,
        headline: application.headline,
        skills,
        bio: application.bio,
        yearsExperience: application.yearsExperience,
        availability: application.availability,
        email: application.email,
        location: application.location,
        links: application.links,
        resumeUrl: application.resumeUrl,
        notableProjects: application.notableProjects,
        uid: application.uid,
      });
    }

    await updateDoc(doc(this.firestore, APPLICATIONS, applicationId), {
      status: 'approved' as PartnerApplicationStatus,
      reviewedBy: adminUid,
      reviewedAt: serverTimestamp(),
      collaboratorId,
      updatedAt: serverTimestamp(),
    });

    return collaboratorId;
  }

  /** Rejects a pending application. `reason` is optional — pass it if your
   *  admin UI collects one; it's surfaced back on the application doc for
   *  the applicant-facing status page to show (or not — that's a UI
   *  decision, not this service's). */
  async reject(applicationId: string, adminUid: string, reason?: string): Promise<void> {
    const application = await this.getById(applicationId);
    if (!application) throw new Error('Application not found.');
    if (application.status !== 'pending') {
      throw new Error(`Only pending applications can be rejected (current status: ${application.status}).`);
    }

    await updateDoc(doc(this.firestore, APPLICATIONS, applicationId), {
      status: 'rejected' as PartnerApplicationStatus,
      reviewedBy: adminUid,
      reviewedAt: serverTimestamp(),
      rejectionReason: reason ?? null,
      updatedAt: serverTimestamp(),
    });
  }

  /* ── Helpers ───────────────────────────────────────────────────────── */

  /**
   * Pulls skills for the resulting Collaborator record out of this role's
   * 'chips'-type roleFieldValues (e.g. Developer's primaryStack, QA's
   * testingTools) — generic across every role's config, so a new chip
   * field on any role automatically feeds into skills with no code change.
   */
  private deriveSkills(application: PartnerApplication): string[] {
    const chipFieldIds = this.roleConfig
      .getFields(application.role)
      .filter((f) => f.type === 'chips' || f.type === 'multiselect')
      .map((f) => f.id);

    const collected = new Set<string>();
    for (const id of chipFieldIds) {
      const value = application.roleFieldValues[id];
      if (Array.isArray(value)) {
        for (const v of value) {
          if (typeof v === 'string' && v.trim()) collected.add(v.trim());
        }
      }
    }
    return Array.from(collected);
  }

  /**
   * Normalizes a raw Firestore doc into PartnerApplication — in particular
   * converting Firestore Timestamp fields (createdAt/updatedAt/reviewedAt)
   * into ISO strings, since the model types these as `string`. Duck-typed
   * (`typeof value?.toDate === 'function'`) rather than importing Timestamp
   * directly, so this tolerates either a real Timestamp or an
   * already-converted string without caring which.
   */
  private toApplication(id: string, data: any): PartnerApplication {
    const toIso = (value: any): string | undefined => {
      if (!value) return undefined;
      if (typeof value?.toDate === 'function') return value.toDate().toISOString();
      return value as string;
    };

    return {
      id,
      uid: data.uid,
      email: data.email,
      fullName: data.fullName,
      phone: data.phone ?? undefined,
      location: data.location ?? undefined,
      role: data.role,
      headline: data.headline,
      bio: data.bio,
      yearsExperience: data.yearsExperience,
      availability: data.availability,
      resumeUrl: data.resumeUrl,
      links: data.links ?? [],
      notableProjects: data.notableProjects ?? [],
      roleFieldValues: data.roleFieldValues ?? {},
      status: data.status,
      reviewedBy: data.reviewedBy ?? undefined,
      reviewedAt: toIso(data.reviewedAt),
      rejectionReason: data.rejectionReason ?? undefined,
      collaboratorId: data.collaboratorId ?? undefined,
      createdAt: toIso(data.createdAt) ?? '',
      updatedAt: toIso(data.updatedAt) ?? '',
    };
  }
}